import { EmojiConfig } from "./Emoji.types";
import {
  animate,
  generatePhysics,
  getRandomInt,
} from "../../functions/helpers";
import { Particle } from "../../Main.types";

const defaultEmoji = ["🤓", "😊", "🥳"];
const factors = [-0.6, -0.3, 0, 0.3, 0.6];

const createElements = (
  root: Element,
  elementCount: number,
  elementSize: number,
  zIndex: number,
  position: string,
  emojis: string[]
) =>
  Array.from({ length: elementCount }).map((_, index) => {
    const element = document.createElement("span");
    element.innerHTML = emojis[index % emojis.length];
    element.style.fontSize = `${elementSize + getRandomInt(0, 4)}px`;
    element.style.position = position;
    element.style.zIndex = `${zIndex}`;
    root.appendChild(element);
    return { element, differentiator: getRandomInt(0, factors.length) };
  });

const updateParticle = (
  particle: Particle,
  progress: number,
  decay: number,
  lifetime: number
) => {
  const {
    x: x0,
    y: y0,
    tiltAngle: tiltAngle0,
    angle2D,
    velocity: velocity0,
    differentiator,
    wobble: wobble0,
  } = particle.physics;

  // If decay = 1, no velocity decay, integration simplifies.
  // Otherwise, integrate velocity with exponential decay over [0, progress].
  const velocityDecayTerm =
    decay !== 1 && decay > 0
      ? (decay ** progress - 1) / Math.log(decay)
      : progress; // If decay=1, integral is just velocity0 * progress.

  // Integrate positions:
  // X due to velocity:
  const currentX = x0 + Math.cos(angle2D) * velocity0 * velocityDecayTerm;

  // Y due to velocity and constant increments:
  // Assume ~60 frames per second:
  const assumedFrames = 60 * (lifetime / 1000);
  // Over the entire animation, y would have been incremented by 5 each frame:
  // Total increment at p=1 is 5 * assumedFrames. Scale by progress:
  const currentY =
    y0 +
    Math.sin(angle2D) * velocity0 * velocityDecayTerm +
    5 * assumedFrames * progress;

  // Tilt angle increment:
  const currentTiltAngle = tiltAngle0 + 0.05 * assumedFrames * progress;

  // Wobble remains constant as there's no increment
  const currentWobble = wobble0;

  // Assuming factors array is available in scope
  const wobbleX =
    currentX +
    (factors[differentiator] * progress * currentWobble * currentWobble +
      20 * Math.sin(currentWobble / 4));

  // Update the transform:
  particle.element.style.transform = `translate3d(${wobbleX}px, ${currentY}px, 0) rotate3d(0, 0, 1, ${
    differentiator % 2 ? currentTiltAngle : -1 * currentTiltAngle
  }rad)`;

  // Opacity changes after halfway point:
  if (progress > 0.5) {
    particle.element.style.opacity = `${2 - 2 * progress}`;
  }
};
export const emoji = (
  root: Element,
  internalAnimatingCallback: () => void,
  config?: EmojiConfig
) => {
  const options = config || {};
  const {
    elementCount = 20,
    elementSize = 25,
    emoji: emojiArray = defaultEmoji,
    angle = 90,
    spread = 45,
    decay = 0.94,
    lifetime = 200,
    startVelocity = 35,
    zIndex = 0,
    position = "fixed",
    onAnimationComplete,
  } = options;
  const spanElements = createElements(
    root,
    elementCount,
    elementSize,
    zIndex,
    position,
    emojiArray
  );
  const particles = spanElements.map(({ element, differentiator }) => ({
    element,
    physics: generatePhysics(angle, spread, startVelocity, differentiator),
  }));

  const onFinish = () => {
    if (typeof onAnimationComplete === "function") {
      onAnimationComplete();
    }
    internalAnimatingCallback();
  };

  animate({
    root,
    particles,
    decay,
    lifetime,
    updateParticle,
    onFinish,
  });
};
